import { useState } from "react";
import { discussions } from "@/mocks/homeData";

const avatarTone: Record<string, string> = {
  primary: "bg-primary-500 text-background-50",
  secondary: "bg-secondary-500 text-background-50",
  accent: "bg-accent-600 text-background-50",
};

const tagTone: Record<string, string> = {
  primary: "bg-primary-100 text-primary-800",
  secondary: "bg-secondary-100 text-secondary-800",
  accent: "bg-accent-100 text-accent-800",
};

export default function DiscussionWall() {
  const [draft, setDraft] = useState("");
  const [hint, setHint] = useState("");

  const handlePost = () => {
    if (!draft.trim()) {
      setHint("先写点什么再发布吧～");
      return;
    }
    setHint("演示页面：你的看法已记录在本地，不会真实提交。");
  };

  return (
    <section id="discussion" className="w-full bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs md:text-sm font-medium text-primary-700 bg-primary-100 px-3 py-1 rounded-full">
            <i className="ri-chat-smile-2-line"></i>
            <span>社区讨论</span>
          </div>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold tracking-tight text-foreground-950">
            这里有认真聊事的氛围
          </h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
            有人补细节，有人泼冷水，也有人负责把线索串起来。
            讨论区欢迎理性发言，反对把猜测当结论。
          </p>
        </div>

        <div className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          <div className="lg:col-span-7 flex flex-col gap-4">
            {discussions.map((item) => (
              <article
                key={item.id}
                className="rounded-lg border border-background-200/80 bg-background-100 p-5 md:p-6 hover:border-primary-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center font-heading text-lg font-bold ${avatarTone[item.tone] ?? avatarTone.primary}`}
                  >
                    {item.initial}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground-900">{item.name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${tagTone[item.tone] ?? tagTone.primary}`}>
                        {item.tag}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-500">{item.time}</span>
                  </div>
                </div>

                <p className="mt-3.5 text-sm md:text-base leading-relaxed text-foreground-800">{item.content}</p>

                <div className="mt-4 flex items-center gap-5 text-xs md:text-sm text-foreground-500">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-thumb-up-line"></i>
                    {item.likes}
                  </button>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-message-3-line"></i>
                    {item.replies} 条回复
                  </span>
                  <a
                    href="#contact"
                    className="ml-auto inline-flex items-center gap-1 text-primary-700 hover:text-primary-800 font-medium whitespace-nowrap cursor-pointer"
                  >
                    回复
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 flex flex-col gap-5">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950">说说你的看法</h3>
                <p className="mt-2 text-xs md:text-sm text-foreground-600">
                  理性发言，别带节奏。涉及具体线索请走官方投稿入口。
                </p>
                <textarea
                  value={draft}
                  maxLength={200}
                  onChange={(event) => {
                    setDraft(event.target.value);
                    setHint("");
                  }}
                  placeholder="聊聊你对这条线索的看法……"
                  className="mt-4 w-full h-28 resize-none rounded-md border border-background-300/70 bg-background-100 px-3.5 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-xs text-foreground-500">{draft.length}/200</span>
                  <button
                    type="button"
                    onClick={handlePost}
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-4 py-2.5 rounded-md whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-send-plane-fill"></i>
                    <span>发布看法</span>
                  </button>
                </div>
                {hint && (
                  <p className="mt-3 text-xs text-foreground-600 bg-background-100 border border-background-200/70 rounded-md px-3 py-2">
                    {hint}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-secondary-100 p-4 text-center">
                  <div className="font-heading text-xl md:text-2xl font-black text-secondary-800">3.2k</div>
                  <div className="mt-1 text-[11px] md:text-xs text-secondary-700">今日讨论</div>
                </div>
                <div className="rounded-lg bg-primary-100 p-4 text-center">
                  <div className="font-heading text-xl md:text-2xl font-black text-primary-800">586</div>
                  <div className="mt-1 text-[11px] md:text-xs text-primary-700">新增线索</div>
                </div>
                <div className="rounded-lg bg-accent-100 p-4 text-center">
                  <div className="font-heading text-xl md:text-2xl font-black text-accent-800">92%</div>
                  <div className="mt-1 text-[11px] md:text-xs text-accent-700">受理率</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}